import React from 'react';
import { InboxIcon, CalendarIcon, ClockIcon, CheckCircleIcon, UserIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { Ticket } from '../data/mockData';
import { getTicketMetrics } from '../utils/ticketMetrics';
interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  iconColorClass: string;
  trend: number;
  trendLabel: string;
}
function KPICard({
  title,
  value,
  icon: Icon,
  iconColorClass,
  trend,
  trendLabel
}: KPICardProps) {
  const isPositiveTrend = trend >= 0;
  // For some metrics like response time, lower is better. We'll simplify and just show red/green based on the metric context.
  // Assuming positive trend number is "increase", negative is "decrease".
  // We'll pass the color directly or compute it. Let's compute a simple color:
  const trendColor = title.includes('Time') ? isPositiveTrend ? 'text-red-600' : 'text-green-600' : isPositiveTrend ? 'text-green-600' : 'text-red-600';
  return <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col" data-id="element-4" data-mp="1v3r6yfivwuzw">
      <div className="flex justify-between items-start mb-4" data-id="element-5" data-mp="1q70u012dmfip">
        <div className={`p-2.5 rounded-xl ${iconColorClass}`} data-id="element-6" data-mp="1lezi4g94eqt7">
          <Icon className="w-6 h-6" data-id="element-7" data-mp="1ldekghrierr4" />
        </div>
        <div className={`flex items-center text-sm font-medium ${trendColor}`} data-id="element-8" data-mp="1ggy07ynfkxjb">
          {isPositiveTrend ? <TrendingUpIcon className="w-4 h-4 mr-1" data-id="element-9" data-mp="1bi4j7w8xqoee" /> : <TrendingDownIcon className="w-4 h-4 mr-1" data-id="element-10" data-mp="17fq15ed3bso6" />}
          {Math.abs(trend)}%
        </div>
      </div>
      <div data-id="element-11" data-mp="12flndw3khc1n">
        <h3 className="text-gray-500 text-sm font-medium mb-1" data-id="element-12" data-mp="12h6efbqoacur">{title}</h3>
        <div className="text-2xl font-bold text-gray-900" data-id="element-13" data-mp="xhtwn5enrvz1">{value}</div>
        <p className="text-xs text-gray-400 mt-1" data-id="element-14" data-mp="2wuedxgplfcmq">{trendLabel}</p>
      </div>
    </div>;
}
type KPICardsProps = {
  tickets: Ticket[];
};
export function KPICards({
  tickets
}: KPICardsProps) {
  const metrics = getTicketMetrics(tickets);
  const kpis = [{
    title: 'Total Active Tickets',
    value: metrics.activeTickets,
    icon: InboxIcon,
    iconColorClass: 'bg-blue-100 text-blue-600',
    trend: 5,
    trendLabel: 'vs last week'
  }, {
    title: 'Received Today',
    value: metrics.receivedToday,
    icon: CalendarIcon,
    iconColorClass: 'bg-indigo-100 text-indigo-600',
    trend: 12,
    trendLabel: 'vs yesterday'
  }, {
    title: 'Avg First Response',
    value: `${Math.round(metrics.avgFirstResponse)}m`,
    icon: ClockIcon,
    iconColorClass: 'bg-cyan-100 text-cyan-600',
    trend: -15,
    trendLabel: 'vs last week'
  }, {
    title: 'Avg Resolution Time',
    value: `${metrics.avgResolutionTime.toFixed(1)}h`,
    icon: CheckCircleIcon,
    iconColorClass: 'bg-teal-100 text-teal-600',
    trend: -8,
    trendLabel: 'vs last week'
  }, {
    title: 'Waiting for Response',
    value: metrics.waitingForResponse,
    icon: UserIcon,
    iconColorClass: 'bg-purple-100 text-purple-600',
    trend: 4,
    trendLabel: 'vs yesterday'
  }, {
    title: 'Closed Today',
    value: metrics.closedToday,
    icon: CheckCircleIcon,
    iconColorClass: 'bg-green-100 text-green-600',
    trend: 18,
    trendLabel: 'vs yesterday'
  }, {
    title: 'Avg Ticket Age',
    value: formatDuration(metrics.avgActiveAge),
    icon: ClockIcon,
    iconColorClass: 'bg-teal-100 text-teal-600',
    trend: -5,
    trendLabel: 'vs last week'
  }];
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-15" data-mp="1wrywpkfnb69d">
      {kpis.map(kpi => <KPICard key={kpi.title} {...kpi} data-id="element-16" data-mp="1s1iezo4skyh7" />)}
    </div>;
}
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}